import { Fragment, useState } from 'react';
import {
  Modal,
  Typography,
  FormItem,
  TextArea,
  Icon,
  IconButton,
  FormLabel,
} from '@getsynapse/design-system';
import intl from 'react-intl-universal';
import { Request } from 'utils/customTypes';
import { REQUEST_STATUS, PATHS } from 'utils/constants';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { formatRequestIdentifier } from 'Pages/helpers';
import { updateRequest } from 'state/ActiveRequest/activeRequestSlice';
import { getRequestPermissions } from 'state/normalized/requestPermissions/requestPermissionsSlice';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';

/** Minimal request shape for the modal (detail page Request or list row request) */
export type ConfirmCancellationRequestData = Pick<Request, 'id'> & {
  requestIdentifier?: string;
  number?: string;
  data?: {
    cancellation?: {
      reason?: string;
      details?: string;
    };
  };
};

type ConfirmCancellationModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  requestData: ConfirmCancellationRequestData;
  /** When provided, called with ldRemark instead of dispatching updateRequest (e.g. when used from table row) */
  onConfirm?: (ldRemark: string) => void | Promise<void>;
};

const getRequestDisplayId = (requestData: ConfirmCancellationRequestData) =>
  formatRequestIdentifier(
    requestData.requestIdentifier ?? requestData.number ?? ''
  );

const ConfirmCancellationModal = ({
  isOpen,
  setIsOpen,
  requestData,
  onConfirm,
}: ConfirmCancellationModalProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [ldRemark, setldRemark] = useState('');

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleConfirmCancellation = async () => {
    if (onConfirm) {
      await Promise.resolve(onConfirm(ldRemark));
      setIsOpen(false);
      return;
    }
    await dispatch(
      updateRequest({
        id: requestData.id!,
        changes: {
          ...requestData,
          status: REQUEST_STATUS.CANCELED,
          data: {
            ...requestData.data,
            cancellation: {
              ...requestData.data?.cancellation,
              ldRemark,
            },
          },
        } as Request,
      })
    );
    await dispatch(getRequestPermissions({ requestIds: [requestData.id!] }));
    setIsOpen(false);
    history.push(PATHS.REQUESTS_LIST_PAGE);
    dispatch(
      showSnackbarNotification({
        notificationVariant: 'success',
        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
        notificationMessage: intl.get(
          'REQUEST_PAGE.NOTIFICATIONS.REQUEST_CANCELLATION_CONFIRMATION',
          {
            requestNo: getRequestDisplayId(requestData),
          }
        ),
      })
    );
  };

  return (
    <Modal
      aria-label='confirm-cancellation-request-modal'
      actionButtons={[
        {
          children: intl.get(
            'REQUEST_PAGE.MODAL.CONFIRM_CANCELLATION_REQUEST.APPROVE_CANCELLATION'
          ),
          variant: 'primary',
          color: 'danger',
          onClick: handleConfirmCancellation,
          'data-cy': 'confirm-cancellation-button',
        },
        {
          children: intl.get(
            'REQUEST_PAGE.MODAL.CONFIRM_CANCELLATION_REQUEST.CANCEL_BUTTON'
          ),
          variant: 'tertiary',
          onClick: handleClose,
          'data-cy': 'cancel-cancellation-button',
        },
      ]}
      closeModal={handleClose}
      isOpen={isOpen}
      size='medium'
      childrenClassName='pr-0 h-auto'
      data-testid='confirm-cancellation-modal'
    >
      <div className='relative mb-4'>
        <IconButton
          onClick={handleClose}
          className='absolute top-0 right-0 text-primary-dark'
          aria-label={intl.get('CLOSE') || 'Close'}
          data-cy='close-modal-button'
          name='close'
        />
        <div className='flex items-center gap-2'>
          <Icon name='close-circle' className='text-error-dark' />
          <Typography
            className='text-lg font-normal leading-[28px]'
            data-cy='change-form-button'
          >
            {intl.get('REQUEST_PAGE.MODAL.CONFIRM_CANCELLATION_REQUEST.TITLE')}
          </Typography>
        </div>
      </div>
      <div className='mb-4' data-testid='request-cancellation-reasons'>
        <Typography>
          {intl.get(`REQUEST_PAGE.MODAL.CONFIRM_CANCELLATION_REQUEST.REASONS`)}
        </Typography>
        <Typography className='font-bold'>
          {requestData.data?.cancellation?.reason
            ? intl.get(
                `REQUESTS_LIST_PAGE.MODAL.CANCEL_REQUEST.REASONS.${requestData.data.cancellation.reason}`
              )
            : ''}
          .
        </Typography>
      </div>
      {requestData.data?.cancellation?.reason === 'OTHER' && (
        <Fragment>
          <FormLabel className='mt-4'>
            {intl.get(
              'REQUEST_PAGE.MODAL.CONFIRM_CANCELLATION_REQUEST.ADDITIONAL_INFORMATION'
            )}
          </FormLabel>
          <Typography variant='body2'>
            {requestData.data?.cancellation.details}
          </Typography>
        </Fragment>
      )}
      <Typography className='mb-4' data-testid='request-cancellation-hint'>
        {intl.get('REQUEST_PAGE.MODAL.CONFIRM_CANCELLATION_REQUEST.NOTICE')}
      </Typography>
      <FormItem
        label={intl.get(
          'REQUEST_PAGE.MODAL.CONFIRM_CANCELLATION_REQUEST.COMMENTS'
        )}
      >
        <TextArea
          textAreaProps={{
            placeholder: intl.get(
              'REQUEST_PAGE.MODAL.CONFIRM_CANCELLATION_REQUEST.REMARK_PLACEHOLDER'
            ),
            className: 'max-h-24',
            'data-cy': 'ld-remark-input',
          }}
          onChange={(event) => setldRemark(event.target.value)}
        />
      </FormItem>
    </Modal>
  );
};

export default ConfirmCancellationModal;
